import React, { useMemo } from 'react'
import { DcChart, DcDiv, DcFlexBox, DcText, colors } from 'pixel-base'
import { InventoryProduct } from './types'

interface DeadStockChartProps {
  data: InventoryProduct[]
  loading: boolean
  error: string | null
  onProductClick?: (product: InventoryProduct) => void
  showCardsOnly?: boolean
  onTitleClick?: () => void
}

const DeadStockChart: React.FC<DeadStockChartProps> = ({
  data,
  loading,
  error,
  onProductClick,
  showCardsOnly = false,
  onTitleClick,
}) => {
  // Transform data for critical alert cards
  const criticalAlerts = useMemo(() => {
    if (!data || data.length === 0) {
      return []
    }

    return data.slice(0, 9).map((product) => {
      const daysInInventory = 0 // Placeholder - not available in current data structure
      const inventoryValue = product.total_inventory_value || 0

      let alertLevel: 'critical' | 'high' | 'medium' = 'medium'
      let alertColor = colors.warning

      if (inventoryValue > 5000 || daysInInventory > 120) {
        alertLevel = 'critical'
        alertColor = colors.danger
      } else if (inventoryValue > 2000 || daysInInventory > 90) {
        alertLevel = 'high'
        alertColor = colors.warning
      }

      return {
        ...product,
        alertLevel,
        alertColor,
        daysInInventory,
        inventoryValue,
      }
    })
  }, [data])

  // Transform data for pie chart (inventory value distribution)
  const pieData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        series: [
          {
            name: 'Dead Stock Value',
            type: 'pie',
            data: [],
            itemStyle: {
              color: colors.danger,
            },
          },
        ],
        totalValue: 0,
      }
    }

    const products = data.slice(0, 8) // Show top 8 for pie chart
    const totalValue = products.reduce(
      (sum, product) => sum + (product.total_inventory_value || 0),
      0,
    )

    const pieData = products.map((product, index) => ({
      name: product.product_name,
      value: product.total_inventory_value || 0,
      itemStyle: {
        color: `hsl(${0 + index * 20}, 70%, 50%)`, // Red gradient
      },
    }))

    return {
      series: [
        {
          name: 'Dead Stock Value',
          type: 'pie',
          radius: '70%',
          data: pieData,
          label: {
            show: true,
            formatter: '{b}: {d}%',
            fontSize: 12,
          },
          labelLine: {
            show: true,
          },
        },
      ],
      totalValue,
    }
  }, [data])

  const pieOptions = useMemo(
    () => ({
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const product = data.find((p) => p.product_name === params.name)
          const variationsHtml =
            product?.variations
              ?.map(
                (variation) =>
                  `<div style="margin: 4px 0; padding: 4px; background: rgba(0,0,0,0.05); border-radius: 3px;">
              <strong>${variation.sku}</strong> - ${
                    variation.current_stock
                  } units<br/>
              <small>Price: ₨${variation.sell_price.toLocaleString()}</small>
            </div>`,
              )
              .join('') || ''

          return `
            <div style="padding: 8px; max-width: 300px;">
              <strong>${params.name}</strong><br/>
              <strong>Total Value: ₨${params.value.toLocaleString()}</strong><br/>
              <strong>Percentage: ${params.percent}%</strong><br/>
              <hr style="margin: 8px 0; border: 1px solid #eee;">
              <strong>Variations:</strong><br/>
              ${variationsHtml}
            </div>
          `
        },
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        top: 'center',
        textStyle: {
          fontSize: 12,
        },
      },
      series: pieData.series,
    }),
    [pieData, data],
  )

  if (loading) {
    return (
      <DcDiv
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DcText>Loading inventory data...</DcText>
      </DcDiv>
    )
  }

  if (error) {
    return (
      <DcDiv
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DcText color={colors.danger}>Error loading data: {error}</DcText>
      </DcDiv>
    )
  }

  // Show only cards
  if (showCardsOnly) {
    return (
      <DcDiv>
        <DcDiv
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px',
          }}
        >
          {criticalAlerts.map((product, _index) => (
            <DcDiv
              key={product.product_id}
              style={{
                padding: '12px',
                borderRadius: '6px',
                backgroundColor: colors.danger,
                color: 'white',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
                border: `2px solid ${colors.danger}`,
                position: 'relative',
              }}
              hoverStyle={{
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
              onClick={() => onProductClick?.(product)}
            >
              {/* Alert level indicator */}
              <DcDiv
                style={{
                  position: 'absolute',
                  top: '6px',
                  right: '6px',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  opacity: 0.8,
                }}
              />

              {/* Product Image */}
              {product.variations?.[0]?.image && (
                <DcDiv
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '3px',
                    marginBottom: '6px',
                    backgroundImage: `url(${product.variations[0].image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    border: '1px solid rgba(255,255,255,0.2)',
                  }}
                />
              )}

              <DcText
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  marginBottom: '4px',
                }}
              >
                {product.current_stock}
              </DcText>
              <DcText
                style={{
                  fontSize: '10px',
                  marginBottom: '2px',
                  opacity: 0.9,
                }}
              >
                {product.product_name.length > 15
                  ? `${product.product_name.substring(0, 15)}...`
                  : product.product_name}
              </DcText>
              <DcText
                style={{
                  fontSize: '8px',
                  opacity: 0.8,
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
              >
                Dead Stock
              </DcText>
            </DcDiv>
          ))}
        </DcDiv>
      </DcDiv>
    )
  }

  // Show both (original layout)
  return (
    <DcDiv>
      <DcFlexBox gap="20px" height="auto">
        {/* Primary Chart - Critical Alert Dashboard */}
        <DcDiv style={{ width: '60%' }}>
          <DcText
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: colors.danger,
              cursor: onTitleClick ? 'pointer' : 'default',
            }}
            onClick={onTitleClick}
          >
            🚨 Critical Dead Stock Alerts
          </DcText>
          <DcDiv
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '16px',
            }}
          >
            {criticalAlerts.map((product, _index) => (
              <DcDiv
                key={product.product_id}
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  backgroundColor: product.alertColor,
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                  border: `3px solid ${product.alertColor}`,
                  position: 'relative',
                }}
                hoverStyle={{
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
                onClick={() => onProductClick?.(product)}
              >
                {/* Alert level indicator */}
                <DcDiv
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    opacity: 0.8,
                  }}
                />

                {/* Product Image */}
                {product.variations?.[0]?.image && (
                  <DcDiv
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      backgroundImage: `url(${product.variations[0].image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      border: '1px solid rgba(255,255,255,0.2)',
                    }}
                  />
                )}

                <DcText
                  style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                  }}
                >
                  ${(product.inventoryValue / 1000).toFixed(0)}k
                </DcText>
                <DcText
                  style={{
                    fontSize: '12px',
                    marginBottom: '4px',
                    opacity: 0.9,
                  }}
                >
                  {product.product_name.length > 20
                    ? `${product.product_name.substring(0, 20)}...`
                    : product.product_name}
                </DcText>
                <DcText
                  style={{
                    fontSize: '10px',
                    opacity: 0.8,
                    marginBottom: '4px',
                  }}
                >
                  {product.daysInInventory} days old
                </DcText>
                <DcText
                  style={{
                    fontSize: '10px',
                    opacity: 0.8,
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                  }}
                >
                  {product.alertLevel}
                </DcText>
              </DcDiv>
            ))}
          </DcDiv>
        </DcDiv>

        {/* Secondary Chart - Pie Chart */}
        <DcDiv style={{ width: '40%' }}>
          <DcDiv style={{ position: 'relative' }}>
            <DcChart
              type="pie"
              data={pieData}
              options={pieOptions}
              height={300}
              width="100%"
              title="Dead Stock Value Distribution"
              isLoading={loading}
              isTitleClickable={!!onTitleClick}
              onClickTitle={onTitleClick}
            />
            {/* Center total value display */}
            <DcDiv
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
              }}
            >
              <DcText
                style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: colors.danger,
                }}
              >
                ${pieData.totalValue.toLocaleString()}
              </DcText>
              <DcText
                style={{
                  fontSize: '12px',
                  color: colors.grayMedium,
                }}
              >
                Dead Stock Value
              </DcText>
            </DcDiv>
          </DcDiv>
        </DcDiv>
      </DcFlexBox>
    </DcDiv>
  )
}

export default DeadStockChart
